/**
 * Transforms the curriculum service response into the shape that
 * downstream components expect from the legacy route-service +
 * content-service pipeline.
 *
 * Legacy shape contract:
 *   routeContent.route   → { entryId, queryType, app, ...rest }
 *   routeContent.content → { [queryType]: { displayTitle, ...pageFields } }
 *
 * Curriculum service shape:
 *   route → { pageExternalId, routeElementExternalId, pageType, ...rest }
 *   page  → { ...wrapperFields, page: { id, type, title, ...contentfulFields } }
 *
 * As of curriculum-service the page no longer splits Contentful entries into
 * `sections[]` + `metadata{}`. `normalizeContentfulPage` now spreads the entry
 * fields directly onto the page object alongside `id`, `type`, and `title`
 * (mirroring content-service's `sanitizeGraphQL`). This transform therefore
 * only needs to:
 *   1. camelCase the snake_case keys the service serializes, and
 *   2. adapt individual-resource (learningExperience) pages to the canonical
 *      entry shape via `transformIndividualResourceEntry`.
 */
import {
  isIndividualResourceEntry,
  transformIndividualResourceEntry,
} from 'layers/content/v3/transforms/transformIndividualResourceEntry'

/**
 * Converts a snake_case string to camelCase.
 */
function snakeToCamel(str) {
  return str.replace(/_([a-z0-9])/g, (_, ch) => ch.toUpperCase())
}

/**
 * Recursively converts all snake_case object keys to camelCase. Idempotent on
 * already-camelCase keys, so it is safe to apply regardless of how the service
 * serializes a given segment. Leaves arrays, primitives, and null unchanged.
 */
function camelizeKeys(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(camelizeKeys)

  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    result[snakeToCamel(key)] = camelizeKeys(value)
  }
  return result
}

/**
 * Maps the curriculum route object to the legacy route shape.
 *
 * The queryType (content type) comes from the route's pageType, the page
 * segment's pageType, or — as a last resort — the inner Contentful page blob's
 * `type`.
 */
export function transformRoute({ curriculumRoute, pageResult, app }) {
  if (!curriculumRoute) return null

  const route = camelizeKeys(curriculumRoute)
  const camelPageResult = camelizeKeys(pageResult)

  const entryId = route.pageExternalId || route.routeElementExternalId

  const queryType =
    route.pageType ||
    camelPageResult?.pageType ||
    camelPageResult?.page?.type ||
    'navigationPage'

  return {
    ...route,
    entryId,
    queryType,
    app,
  }
}

/**
 * Maps the curriculum page payload to the legacy content shape, keyed by
 * queryType:
 *   { navigationPage: { displayTitle: "My Page", ... } }
 *
 * The inner Contentful blob (`pageResult.page`, or `pageResult` itself when
 * flat) is camelCased and, for individual-resource pages, adapted to the
 * canonical entry shape that the resource components consume.
 */
export function transformContent({ pageResult, queryType, curriculumRoute }) {
  if (!pageResult) return {}

  const camelPageResult = camelizeKeys(pageResult)
  const pageData = camelPageResult?.page || camelPageResult

  const transformedPageData = isIndividualResourceEntry({
    pageEntry: pageData,
    queryType,
    curriculumRoute,
  })
    ? transformIndividualResourceEntry(pageData)
    : pageData

  return {
    [queryType]: transformedPageData,
  }
}

/**
 * Combines route and content transforms into a single routeContent object
 * matching the legacy shape consumed by PagesV2, FamilyResources, etc.
 */
export function transformCurriculumResponse({
  curriculumRoute,
  pageResult,
  app,
  loading,
  error,
  changeLocale,
  locale,
  coursePointer,
  contentGroupCode,
}) {
  const route = transformRoute({ curriculumRoute, pageResult, app })
  const queryType = route?.queryType || 'navigationPage'
  const content = transformContent({ pageResult, queryType, curriculumRoute })

  return {
    route,
    content,
    loading: loading || (!error && !route),
    error,
    changeLocale,
    locale,
    coursePointer,
    contentGroupCode,
  }
}
